import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import heic2any from "heic2any";
import { Link, useNavigate } from "react-router-dom";
import { GroupsOutlined } from "@mui/icons-material";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import { useStoreDispatch } from "@app/hooks";
import { setGroups, RootState, setShowAlert, store } from "@app/store";
import { GroupsRequestDef } from "@app/types";
import {
  apiAddGroup,
  apiArchiveGroupsInBulk,
  apiDeleteGroupsInBulk,
  apiGetGroup,
  apiGroups,
  apiArchivedGroups,
  apiPostTOURecordHistory,
  apiUpdateGroup,
  getTOUVersionMaster,
  getUser,
} from "@app/api";
import {
  clearMachineReportsPreferences,
  handleUploadStore,
  isMobile,
  isValidMediaFile,
  REGEX,
} from "@app/helpers";
import {
  CURRENT_PAGE,
  LIMIT_PAGE,
  TOTAL_PAGE,
  UploadFileType,
} from "@app/constants";
import {
  DataPagination,
  DataTable,
  PageTitle,
  DataUsageDialog,
  DataDialog,
  FileUploadButton,
} from "@app/components/atoms";
import { Helmet } from "@app/components/atoms";
import { useTranslation } from "react-i18next";
import { StorageKey } from "@app/constants";
import { getItem } from "@app/helpers";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { apiGetAnnouncementsPopups } from "@app/api/announcements/announcements.api";
import { Announcements } from "@app/types";
import { AnnouncementPopupModal } from "@app/components/organisms";
import {
  IconActionOverflow,
  IconDelete,
  IconEdit,
  IconPlus,
} from "@app/components/icons";
import { IconDeleteRed } from "@app/components/icons/IconDeleteRed/IconDeleteRed";
import { Button } from "@mui/material";
import { ArchiveOutlined } from "@mui/icons-material";
import { InfoOutlined } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { InputText } from "primereact/inputtext";
import "@app/pages/Features/GroupList/GroupDetail/MachineTab/MachineTab.css";

const GROUP_LOGO_PREVIEW_CARD_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  p: 1,
};

const GROUP_LOGO_PREVIEW_IMAGE_SX = {
  position: "relative" as const,
  width: 120,
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

type groupType = {
  id: string;
  groupName: string;
  companyName: string;
  machineCount: number;
  reportCount: number;
  abnormalityReportCount?: number;
  location: string;
  memberCount: number;
  inspectionFormCount: string;
  pendingInvites: number;
  isSignatureRequired?: boolean;
  permissions: {
    allowEditDeleteGroup: boolean;
  };
};

type groupArchivedType = {
  id: string;
  groupName: string;
  companyName: string;
  machineCount: number;
  reportCount: number;
  abnormalityReportCount?: number;
  location: string;
  memberCount: number;
  inspectionFormCount: string;
  pendingInvites: number;
  isSignatureRequired?: boolean;
  permissions: {
    allowEditDeleteGroup: boolean;
  };
};

export function GroupList() {
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const [page, setPage] = useState(CURRENT_PAGE);
  const [rows, setRow] = useState<groupType[]>();
  const [archivedRows, setArchivedRows] = useState<groupArchivedType[]>();
  const [groupsIsPendingUser, setGroupsIsPendingUser] = useState(false);
  const [groupsIsAbnormality, setGroupsIsAbnormality] = useState(false);
  const [archivedIsPendingUser, setArchivedIsPendingUser] = useState(false);
  const [archivedIsAbnormality, setArchivedIsAbnormality] = useState(false);
  const [field, setField] = useState<string>("");
  const [order, setOrder] = useState<string | undefined>();
  const { t } = useTranslation();
  const [pageInfo, setPageInfo] = useState({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });
  const [pageArchivedInfo, setPageArchivedInfo] = useState({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });
  const [openAnnouncements, setOpenAnnouncements] = useState(false);
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const [openUpdateGroup, setOpenUpdateGroup] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcements[]>();
  const [announcementsLoading, setAnnouncementsLoading] =
    useState<boolean>(false);
  const { suppressDataUsagePopup } = useSelector(
    (state: RootState) => state?.dataUsage,
  );
  const isShownDataUsagePopup = getItem(StorageKey.IS_SHOWN_DATA_USAGE_POPUP);
  const [loadingLoader, setLoadingLoader] = useState(false);
  const [openTOU, setOpenTOU] = useState(false);
  const [openTOUPDF, setOpenTOUPDF] = useState(false);
  const [iframeSrc, setIframeSrc] = useState("");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const urlStack = useRef<string[]>([]);
  const [checkboxTOU, setCheckboxTOU] = useState(false);
  const [checkboxPriPol, setCheckboxPriPol] = useState(false);
  const isButtonDisabled = !(checkboxTOU && checkboxPriPol);
  const [touPdfUrlOpen, setTouPdfUrl] = useState<string>("");
  const [privacyPolicyPdfUrlOpen, setPrivacyPolicyPdfUrl] =
    useState<string>("");
  const [userIsoLocaleCode, setUserIsoLocaleCode] = useState<string>("");
  const [payloadOpenPostTOURecordHistory, setPayloadPostTOURecordHistory] =
    useState({});
  const [groupName, setGroupName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [allowNonKomatsuUsage, setAllowNonKomatsuUsage] = useState(true);
  const [allowUploadFromFS, setAllowUploadFromFS] = useState(true);
  const [isSignatureRequired, setIsSignatureRequired] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [openRemoveLogoConfirm, setOpenRemoveLogoConfirm] = useState(false);
  const [logoMenuAnchor, setLogoMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [logoPreviewViewerOpen, setLogoPreviewViewerOpen] = useState(false);
  const logoPreviewUrlRef = useRef<string>("");
  const [groupId, setGroupId] = useState("");
  const [openFormDeleteBulk, setOpenFormDeleteBulk] = useState(false);
  const [openDeleteGroup, setOpenDeleteGroup] = useState(false);
  const [openArchiveGroup, setOpenArchiveGroup] = useState(false);
  const [openUnArchiveGroup, setOpenUnArchiveGroup] = useState(false);
  const [openMoreInfo, setOpenMoreInfo] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedRowArchivedKeys, setSelectedRowArchivedKeys] = useState<
    string[]
  >([]);
  const [selectedRowKey, setSelectedRowKey] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElArchive, setAnchorElArchive] = useState<null | HTMLElement>(
    null,
  );
  const [targetRow, setTargetRow] = useState<any | null>(null);
  const open = Boolean(anchorEl);
  const openArchive = Boolean(anchorElArchive);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [showArchivedList] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTextClear, setSearchTextClear] = useState("");

  const clearSearch = () => {
    setSearchTextClear("");
  };

  const handleGroupSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key !== "Enter") return;
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    const trimmed = searchTextClear.trim();
    if (!trimmed) return;
    navigate(`/machines/search?q=${encodeURIComponent(trimmed)}`);
  };

  const handleCheckboxChangeTOU = (event: any) => {
    setCheckboxTOU(event.target.checked);
  };

  const handleCheckboxChangePrivacyPolicy = (event: any) => {
    setCheckboxPriPol(event.target.checked);
  };

  const convertDataRow = (data: groupType[]) => {
    return data.map((row: any) => ({
      ...row,
      id: row?.groupId,
    }));
  };

  const convertArchivedDataRow = (data: groupArchivedType[]) => {
    return data.map((row: any) => ({
      ...row,
      id: row?.groupId,
    }));
  };

  const getGroups = useCallback(
    (payload: GroupsRequestDef) => {
      return apiGroups(payload).then((res) => {
        const {
          data,
          meta,
          isPendingUser,
          isAbnormality,
        }: {
          data: groupType[];
          meta: any;
          isPendingUser?: boolean;
          isAbnormality?: boolean;
        } = res.data;
        const formatData = convertDataRow(data);
        setRow(formatData);
        setPageInfo(meta.pageInfo);
        setGroupsIsPendingUser(!!isPendingUser);
        setGroupsIsAbnormality(!!isAbnormality);
        dispatch(setGroups(data));
      });
    },
    [dispatch],
  );

  const getArchivedGroups = useCallback(
    (payload: GroupsRequestDef) => {
      return apiArchivedGroups(payload).then((res) => {
        const {
          data,
          meta,
          isPendingUser,
          isAbnormality,
        }: {
          data: groupArchivedType[];
          meta: any;
          isPendingUser?: boolean;
          isAbnormality?: boolean;
        } = res.data;
        const formatData = convertArchivedDataRow(data);
        setArchivedRows(formatData);
        setPageArchivedInfo(meta.pageInfo);
        setArchivedIsPendingUser(!!isPendingUser);
        setArchivedIsAbnormality(!!isAbnormality);
        dispatch(setGroups(data));
      });
    },
    [dispatch],
  );

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleSort = (order: string | undefined, property: string) => {
    setField(property);
    setOrder(order);
  };

  const handleIframeLoad = () => {
    setLoadingLoader(false);
  };

  const handleClickCloseTOU = () => setOpenTOU(false);

  const handleClickCloseTOUPDF = () => setOpenTOUPDF(false);

  const openDialogWithLink = (link: string) => {
    setLoadingLoader(true);
    urlStack.current.push(link);
    setIframeSrc(link);
    setOpenTOUPDF(true);
    setTimeout(() => {
      setLoadingLoader(false);
    }, 1000);
  };

  const PostTOURecordHistory = async () => {
    try {
      await apiPostTOURecordHistory(payloadOpenPostTOURecordHistory);
      handleClickCloseTOU();
      store.dispatch(setShowAlert(t("touprivacyupdated")));
    } catch (error) {
      console.error("Error while submitting the agreement:", error);
    }
  };

  const getAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const db = await apiGetAnnouncementsPopups();
      setAnnouncements(db.data.data);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const [userTimeFormat, setUserTimeFormat] = useState<string>("");

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const [getUserAPI] = await Promise.all([
          getUser(),
          getGroups({
            limit: LIMIT_PAGE,
            page: CURRENT_PAGE,
            ...(field && order ? { orderBys: [{ field, order }] } : {}),
          }),
          getArchivedGroups({
            limit: LIMIT_PAGE,
            page: CURRENT_PAGE,
            ...(field && order ? { orderBys: [{ field, order }] } : {}),
          }),
        ]);

        if (!getUserAPI?.data?.data)
          throw new Error("Failed to fetch user data");

        const {
          touAgreement,
          privacyPolicyAgreement,
          residenceCountryCode,
          isoLocaleCode,
          timeFormat,
        } = getUserAPI.data.data;

        setUserIsoLocaleCode(isoLocaleCode);
        setUserTimeFormat(timeFormat);

        if (!touAgreement || !privacyPolicyAgreement) {
          if (!residenceCountryCode)
            throw new Error("Residence country code is missing");

          const getTOUVersionMasterAPI =
            await getTOUVersionMaster(residenceCountryCode);

          if (!getTOUVersionMasterAPI?.data?.data?.length) {
            console.warn(
              "No data received from getTOUVersionMaster API, stopping execution.",
            );
          } else {
            const touData = getTOUVersionMasterAPI.data.data.find(
              (item: { type: any }) => item.type === "TOU",
            );
            const privacyPolicyData = getTOUVersionMasterAPI.data.data.find(
              (item: { type: any }) => item.type === "PRIVACY_POLICY",
            );

            if (touData?.pdfUrl) setTouPdfUrl(touData.pdfUrl);
            if (privacyPolicyData?.pdfUrl)
              setPrivacyPolicyPdfUrl(privacyPolicyData.pdfUrl);

            setOpenTOU(true);
            setPayloadPostTOURecordHistory({
              touPrivacy: [
                {
                  touVersionMasterId: touData?.touVersionMasterId,
                  type: touData?.type,
                  version: touData?.version,
                  isoLocaleCode: touData?.isoLocaleCode,
                },
                {
                  touVersionMasterId: privacyPolicyData?.touVersionMasterId,
                  type: privacyPolicyData?.type,
                  version: privacyPolicyData?.version,
                  isoLocaleCode: privacyPolicyData?.isoLocaleCode,
                },
              ],
            });
          }
        }

        await getAnnouncements();
      } catch (error) {
        console.error("Error during initialization:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);
  const handleClick = async (
    event: React.MouseEvent<HTMLElement>,
    target: any,
  ) => {
    setSelectedRowKeys([]);
    setSelectedRowArchivedKeys([]);
    setAnchorEl(event.currentTarget);

    setTargetRow(target);
    setSelectedRowKey([target.id.toString()]);

    try {
      const group = await apiGetGroup(target.id);
      const g = group.data?.data;
      setGroupName(g?.groupName ?? "");
      setCompanyName(g?.companyName ?? "");
      setLocation(g?.location ?? "");
      setAllowNonKomatsuUsage(!!g?.allowNonKomatsuInfoUse);
      setAllowUploadFromFS(!!g?.galleryPermission);
      setIsSignatureRequired(
        target?.isSignatureRequired ?? g?.isSignatureRequired ?? true,
      );
      setGroupId(g?.groupId ?? target?.id ?? "");
      setLogoFile(null);
      setLogoRemoved(false);
      setLogoError(null);
      setLogoImageLoaded(false);
      setLogoPreviewUrl(g?.logoUrl ?? "");
    } catch (error) {
      console.error("Error fetching group:", error);
    }
  };

  const handleClickArchive = async (
    event: React.MouseEvent<HTMLElement>,
    target: any,
  ) => {
    setSelectedRowKeys([]);
    setSelectedRowArchivedKeys([]);

    setAnchorElArchive(event.currentTarget);
    setTargetRow(target);
    setSelectedRowKey([target.id.toString()]);

    try {
      const group = await apiGetGroup(target.id);
      const g = group.data?.data;
      setGroupName(g?.groupName ?? "");
      setCompanyName(g?.companyName ?? "");
      setLocation(g?.location ?? "");
      setAllowNonKomatsuUsage(!!g?.allowNonKomatsuInfoUse);
      setAllowUploadFromFS(!!g?.galleryPermission);
      setIsSignatureRequired(
        target?.isSignatureRequired ?? g?.isSignatureRequired ?? true,
      );
      setGroupId(g?.groupId ?? target?.id ?? "");
      setLogoFile(null);
      setLogoRemoved(false);
      setLogoError(null);
      setLogoImageLoaded(false);
      setLogoPreviewUrl(g?.logoUrl ?? "");
    } catch (error) {
      console.error("Error fetching group:", error);
    }
  };

  const LOGO_MAX_SIZE_BYTES = 5 * 1024 * 1024;

  const resetLogoState = () => {
    if (logoPreviewUrlRef.current) {
      URL.revokeObjectURL(logoPreviewUrlRef.current);
      logoPreviewUrlRef.current = "";
    }
    setLogoFile(null);
    setLogoRemoved(false);
    setLogoPreviewUrl("");
    setLogoImageLoaded(false);
    setLogoError(null);
  };

  const handleLogoFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setLogoError(null);

    const extension = file.name.split(".").pop()?.toLowerCase();
    const fileType = file.type?.toLowerCase();
    const isHeic = fileType === "image/heic" || extension === "heic";
    const isPngOrJpg =
      ["png", "jpg", "jpeg"].includes(extension ?? "") &&
      ["image/png", "image/jpeg", "image/jpg"].includes(fileType ?? "");

    if (!isHeic && !isPngOrJpg) {
      setLogoError(t("messages.wrongImage"));
      return;
    }

    if (file.size > LOGO_MAX_SIZE_BYTES) {
      setLogoError(t("messages.errSizeImage5mb"));
      return;
    }

    let workingFile = file;
    if (isHeic) {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
        });
        workingFile = new File(
          [convertedBlob as Blob],
          file.name.replace(/\.heic$/i, ".jpeg"),
          { type: "image/jpeg" },
        );
      } catch {
        setLogoError(t("machineInfo.errormessage"));
        return;
      }
    }

    const isValidFile = await isValidMediaFile(workingFile);
    if (!isValidFile) {
      setLogoError(t("messages.corruptFileType"));
      return;
    }

    if (logoPreviewUrlRef.current) {
      URL.revokeObjectURL(logoPreviewUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(workingFile);
    logoPreviewUrlRef.current = objectUrl;

    setLogoFile(workingFile);
    setLogoRemoved(false);
    setLogoImageLoaded(false);
    setLogoPreviewUrl(objectUrl);
  };

  const handleRemoveLogo = () => {
    if (logoPreviewUrlRef.current) {
      URL.revokeObjectURL(logoPreviewUrlRef.current);
      logoPreviewUrlRef.current = "";
    }
    setLogoFile(null);
    setLogoPreviewUrl("");
    setLogoImageLoaded(false);
    setLogoRemoved(true);
    setOpenRemoveLogoConfirm(false);
  };

  const handleAddGroup = async () => {
    let logoUrl: string | undefined;
    if (logoFile) {
      const uploaded = await handleUploadStore(
        UploadFileType.GROUP_LOGO,
        logoFile,
      );
      if (!uploaded?.filePath) {
        setLogoError(t("machineInfo.errormessage"));
        return;
      }
      logoUrl = uploaded.filePath;
    }

    const payload = {
      groupName: groupName,
      companyName: companyName,
      location: location,
      allowNonKomatsuInfoUse: allowNonKomatsuUsage,
      galleryPermission: allowUploadFromFS,
      isSignatureRequired: isSignatureRequired,
      ...(logoUrl !== undefined ? { logoUrl } : {}),
    };

    try {
      const response = await apiAddGroup(payload);

      if (response.status === 201) {
        store.dispatch(setShowAlert(t("groupAddedSuccessfully")));

        await getGroups({
          limit: pageInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        await getArchivedGroups({
          limit: pageArchivedInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        setGroupName("");
        setCompanyName("");
        setLocation("");
        setAllowNonKomatsuUsage(true);
        setAllowUploadFromFS(true);
        setIsSignatureRequired(false);
        setErrorMessage(null);
        setCompanyError(null);
        setLocationError(null);
        setSelectedRowKeys([]);
        setSelectedRowArchivedKeys([]);
        resetLogoState();

        setOpenAddGroup(false);
      } else {
        store.dispatch(setShowAlert(t("groupFailed")));
      }
    } catch (error) {
      console.error("Error while adding group:", error);
    } finally {
      setOpenAddGroup(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!groupId) {
      console.error("Group ID is required for updating a group");
      return;
    }

    let logoUrl: string | undefined;
    if (logoFile) {
      const uploaded = await handleUploadStore(
        UploadFileType.GROUP_LOGO,
        logoFile,
      );
      if (!uploaded?.filePath) {
        setLogoError(t("machineInfo.errormessage"));
        return;
      }
      logoUrl = uploaded.filePath;
    } else if (logoRemoved) {
      logoUrl = "";
    }

    const payload = {
      groupName: groupName,
      companyName: companyName,
      location: location,
      allowNonKomatsuInfoUse: allowNonKomatsuUsage,
      galleryPermission: allowUploadFromFS,
      isSignatureRequired: isSignatureRequired,
      ...(logoUrl !== undefined ? { logoUrl } : {}),
    };

    try {
      const response = await apiUpdateGroup(groupId, payload);

      if (response.status === 200) {
        store.dispatch(setShowAlert(t("groupUpdatedSuccessfully")));

        setGroupName("");
        setCompanyName("");
        setLocation("");
        setAllowNonKomatsuUsage(true);
        setAllowUploadFromFS(true);
        setIsSignatureRequired(false);
        setErrorMessage(null);
        setCompanyError(null);
        setLocationError(null);
        setSelectedRowKeys([]);
        setSelectedRowArchivedKeys([]);
        resetLogoState();
        setOpenUpdateGroup(false);

        await getGroups({
          limit: pageInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        await getArchivedGroups({
          limit: pageArchivedInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });
      } else {
        store.dispatch(setShowAlert(t("groupUpdateFailed")));
      }
    } catch (error) {
      console.error("Error while updating group:", error);
    } finally {
      setOpenUpdateGroup(false);
    }
  };

  const handleBulkGroupsDelete = async (selectedIds: string[]) => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      store.dispatch(setShowAlert(t("selectAtLeastOneGroup")));
      return;
    }

    const payload = { groupIds: selectedIds };

    try {
      const response = await apiDeleteGroupsInBulk(payload);
      if (response.status === 200) {
        if (selectedIds.length === 1) {
          store.dispatch(setShowAlert(t("groupDeletedstatus")));
        } else {
          store.dispatch(setShowAlert(t("groupDeletedstatusBulk")));
        }
        setSelectedRowKeys([]);
        setSelectedRowArchivedKeys([]);
        setSelectedRowKey([]);
        await getGroups({
          limit: pageInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        await getArchivedGroups({
          limit: pageArchivedInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        setGroupName("");
        setCompanyName("");
        setLocation("");
        setAllowNonKomatsuUsage(true);
        setAllowUploadFromFS(true);
        setIsSignatureRequired(false);
        setErrorMessage(null);
        setCompanyError(null);
        setLocationError(null);
      }
    } catch (error) {
      console.error(`Error deleting groups`, error);
    } finally {
      setOpenFormDeleteBulk(false);
      setOpenDeleteGroup(false);
      setOpenArchiveGroup(false);
      setOpenUnArchiveGroup(false);
    }
  };

  const handleBulkGroupsUnarchive = async (selectedIds: string[]) => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      store.dispatch(setShowAlert(t("selectAtLeastOneGroup")));
      return;
    }

    const payload = { groupIds: selectedIds, isArchived: false };

    try {
      const response = await apiArchiveGroupsInBulk(payload);
      if (response.status === 200) {
        if (selectedIds.length === 1) {
          store.dispatch(setShowAlert(t("groupUnarchivedstatus")));
        } else {
          store.dispatch(setShowAlert(t("groupUnarchivedstatusBulk")));
        }
        setSelectedRowKeys([]);
        setSelectedRowKey([]);
        setSelectedRowArchivedKeys([]);
        await getGroups({
          limit: pageInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        await getArchivedGroups({
          limit: pageArchivedInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        setGroupName("");
        setCompanyName("");
        setLocation("");
        setAllowNonKomatsuUsage(true);
        setAllowUploadFromFS(true);
        setIsSignatureRequired(false);
        setErrorMessage(null);
        setCompanyError(null);
        setLocationError(null);
      }
    } catch (error) {
      console.error(`Error Unarchiving groups`, error);
    } finally {
      setOpenFormDeleteBulk(false);
      setOpenDeleteGroup(false);
      setOpenArchiveGroup(false);
      setOpenUnArchiveGroup(false);
    }
  };

  const handleBulkGroupsArchive = async (selectedIds: string[]) => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      store.dispatch(setShowAlert(t("selectAtLeastOneGroup")));
      return;
    }

    const payload = { groupIds: selectedIds, isArchived: true };

    try {
      const response = await apiArchiveGroupsInBulk(payload);
      if (response.status === 200) {
        if (selectedIds.length === 1) {
          store.dispatch(setShowAlert(t("groupArchivedstatus")));
        } else {
          store.dispatch(setShowAlert(t("groupArchivedstatusBulk")));
        }
        setSelectedRowKeys([]);
        setSelectedRowKey([]);
        setSelectedRowArchivedKeys([]);
        await getGroups({
          limit: pageInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        await getArchivedGroups({
          limit: pageArchivedInfo.limit,
          page,
          ...(field && order ? { orderBys: [{ field, order }] } : {}),
        });

        setGroupName("");
        setCompanyName("");
        setLocation("");
        setAllowNonKomatsuUsage(true);
        setAllowUploadFromFS(true);
        setIsSignatureRequired(false);
        setErrorMessage(null);
        setCompanyError(null);
        setLocationError(null);
      }
    } catch (error) {
      console.error(`Error Archiving groups`, error);
    } finally {
      setOpenFormDeleteBulk(false);
      setOpenDeleteGroup(false);
      setOpenArchiveGroup(false);
    }
  };

  const renderReportCountCell = (
    value: number,
    record: { groupId: string; abnormalityReportCount?: number },
  ) => (
    <div
      className="txt-nowrap"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        verticalAlign: "middle",
      }}
    >
      <Link
        className="txt-link"
        to={`/groups/${record.groupId}/machines-reports-history`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 1,
        }}
      >
        {value}
      </Link>
      {(record.abnormalityReportCount ?? 0) > 0 && (
        <span
          className="txt-nowrap"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "2px 8px",
            borderRadius: "999px",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            fontSize: "12px",
            fontWeight: 500,
            color: "#424242",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          <CampaignOutlinedIcon
            sx={{ color: "#DA9309", fontSize: 16, display: "block" }}
          />
          {record.abnormalityReportCount}
        </span>
      )}
    </div>
  );

  const baseColumns = [
    {
      key: "groupName",
      title: "formAction.Groups",
      dataIndex: "groupName",
      align: "left",
      sorted: true,
      style: {
        minWidth: isMobile() ? 0 : 0,
        whiteSpace: "nowrap",
      },
      render: (value: any, _record: any) => (
        <Link
          className="txt-link"
          to={`/groups/${_record.groupId}`}
          style={{ whiteSpace: "nowrap" }}
        >
          {value}
        </Link>
      ),
    },
    {
      key: "reportCount",
      title: "groupDetailTab.reportTab",
      dataIndex: "reportCount",
      align: "center",
      sorted: true,
      whiteSpace: "nowrap",
      className: "txt-nowrap",
      style: {
        minWidth: 100,
        whiteSpace: "nowrap",
      },
      render: (value: number, record: any) =>
        renderReportCountCell(value, record),
    },
    {
      key: "machineCount",
      title: "groupDetailTab.machineTab",
      dataIndex: "machineCount",
      align: "center",
      sorted: true,
      whiteSpace: "nowrap",
      style: {
        minWidth: 112,
      },
      render: (value: any, record: any) => (
        <Link className={`txt-link`} to={`/groups/${record.groupId}/machines`}>
          {value}
        </Link>
      ),
    },
    {
      key: "memberCount",
      title: "groupDetailTab.memberTab",
      dataIndex: "memberCount",
      align: "center",
      sorted: true,
      whiteSpace: "nowrap",
      style: {
        minWidth: 111,
      },
      render: (value: any, record: any) => (
        <>
          {record.permissions.allowCreateEditDeleteMember ? (
            <Link
              className={`txt-link`}
              to={`/groups/${record.groupId}/members`}
            >
              {value}

              {record.pendingInvites > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minWidth: "17px",
                    height: "17px",
                    padding: "0 5.5px",
                    borderRadius: "50%",
                    backgroundColor: "#140a9a",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: "600",
                    marginInlineStart: "6px",
                  }}
                >
                  {record.pendingInvites}
                </span>
              )}
            </Link>
          ) : (
            value
          )}
        </>
      ),
    },
    {
      key: "companyName",
      title: "tableGroup.companyName",
      dataIndex: "companyName",
      align: "left",
      style: {
        minWidth: isMobile() ? 209 : 250,
      },
      className: "txt-pre-wrap",
    },
    {
      key: "location",
      title: "tableMachineDetail.location",
      dataIndex: "location",
      align: "left",
      style: {
        minWidth: isMobile() ? 150 : 150,
      },
      className: "txt-pre-wrap",
    },
    {
      key: "id",
      title: "",
      dataIndex: "id",
      render: (_: string, record: any) => (
        <Button
          className="bnt-action-form"
          onClick={(e) => handleClick(e, record)}
        >
          <IconActionOverflow />
        </Button>
      ),
      className: "add",
      width: 0,
      align: "center",
    },
  ];

  const archivedBaseColumns = [
    {
      key: "groupName",
      title: "formAction.Groups",
      dataIndex: "groupName",
      align: "left",
      sorted: true,
      style: {
        minWidth: isMobile() ? 0 : 0,
        whiteSpace: "nowrap",
      },
      render: (value: any, _record: any) => (
        <Link
          className="txt-link"
          to={`/groups/${_record.groupId}`}
          style={{ whiteSpace: "nowrap" }}
        >
          {value}
        </Link>
      ),
    },
    {
      key: "reportCount",
      title: "groupDetailTab.reportTab",
      dataIndex: "reportCount",
      align: "center",
      sorted: true,
      whiteSpace: "nowrap",
      className: "txt-nowrap",
      style: {
        minWidth: 100,
        whiteSpace: "nowrap",
      },
      render: (value: number, record: any) =>
        renderReportCountCell(value, record),
    },
    {
      key: "machineCount",
      title: "groupDetailTab.machineTab",
      dataIndex: "machineCount",
      align: "center",
      sorted: true,
      whiteSpace: "nowrap",
      style: {
        minWidth: 112,
      },
      render: (value: any, record: any) => (
        <Link className={`txt-link`} to={`/groups/${record.groupId}/machines`}>
          {value}
        </Link>
      ),
    },
    {
      key: "memberCount",
      title: "groupDetailTab.memberTab",
      dataIndex: "memberCount",
      align: "center",
      sorted: true,
      whiteSpace: "nowrap",
      style: {
        minWidth: 111,
      },
      render: (value: any, record: any) => (
        <>
          {record.permissions.allowCreateEditDeleteMember ? (
            <Link
              className={`txt-link`}
              to={`/groups/${record.groupId}/members`}
            >
              {value}
              {record.pendingInvites > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minWidth: "17px",
                    height: "17px",
                    padding: "0 5.5px",
                    borderRadius: "50%",
                    backgroundColor: "#140a9a",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: "600",
                    marginInlineStart: "6px",
                  }}
                >
                  {record.pendingInvites}
                </span>
              )}
            </Link>
          ) : (
            value
          )}
        </>
      ),
    },
    {
      key: "companyName",
      title: "tableGroup.companyName",
      dataIndex: "companyName",
      align: "left",
      style: {
        minWidth: isMobile() ? 209 : 250,
      },
      className: "txt-pre-wrap",
    },
    {
      key: "location",
      title: "tableMachineDetail.location",
      dataIndex: "location",
      align: "left",
      style: {
        minWidth: isMobile() ? 150 : 150,
      },
      className: "txt-pre-wrap",
    },
    {
      key: "id",
      title: "",
      dataIndex: "id",
      render: (_: string, record: any) => (
        <Button
          className="bnt-action-form"
          onClick={(e) => handleClickArchive(e, record)}
        >
          <IconActionOverflow />
        </Button>
      ),
      className: "add",
      width: 0,
      align: "center",
    },
  ];

  const columns = [...baseColumns];

  const archivedColumns = [...archivedBaseColumns];

  useEffect(() => {
    clearMachineReportsPreferences();
  }, []);

  useEffect(() => {
    if (announcements && announcements.length > 0) {
      setOpenAnnouncements(true);
    }
  }, [announcements]);

  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);

  const showPendingUserNotice = groupsIsPendingUser || archivedIsPendingUser;
  const showAbnormalityNotice = groupsIsAbnormality || archivedIsAbnormality;

  const renderGroupListNotices = () => (
    <>
      {showPendingUserNotice && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#fff",
            padding: "10px",
            marginBottom: showAbnormalityNotice ? "8px" : "15px",
            borderInlineStart: "5px solid #140a9a",
          }}
        >
          <PersonAddAltOutlinedIcon sx={{ color: "#140a9a" }} />
          <span style={{ marginTop: "2px" }}>
            {t("group.pendingInvitesNotice")}
          </span>
        </div>
      )}
      {showAbnormalityNotice && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#fff",
            padding: "10px",
            marginBottom: "15px",
            borderInlineStart: "5px solid #DA9309",
          }}
        >
          <CampaignOutlinedIcon sx={{ color: "#DA9309" }} />
          <span style={{ marginTop: "2px" }}>
            {t("group.unaddressedAbnormalityNotice")}
          </span>
        </div>
      )}
    </>
  );

  return (
    <div className="group-list-page">
      <Helmet title={t("metaInfo.groupList")} />

      <div className="container">
        <div className="row">
          <div className="mb-25 sp-p24">
            {renderGroupListNotices()}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: { xs: "10px", sm: "12px" },
                mb: "10px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  width: { xs: "100%", sm: "auto" },
                  flex: { xs: "1 1 auto", sm: "1 1 0" },
                  mr: { xs: 0, sm: 1.5 },
                }}
              >
                <PageTitle
                  title="formAction.Groups"
                  className="flex items-center mb-0 text-truncate"
                >
                  <GroupsOutlined />
                </PageTitle>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  width: { xs: "100%", sm: "277px" },
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <InputText
                  type="text"
                  value={searchTextClear}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTextClear(e.target.value)
                  }
                  onKeyDown={handleGroupSearchKeyDown}
                  placeholder={t("acrossGroupMachines.pageTitle")}
                  className="custom-input"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    paddingLeft: "1rem",
                    paddingRight: "2rem",
                    height: "50px",
                    border: "1.5px solid #bcbcbc",
                    borderRadius: "30px",
                    boxSizing: "border-box",
                  }}
                />
                {searchTextClear && (
                  <button
                    className="clear-button"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    type="button"
                  >
                    ×
                  </button>
                )}
              </Box>
            </Box>

            {!showArchivedList && (
              <div
                className="d-flex justify-content-end"
                style={{ marginBottom: "8px" }}
              >
                <Button
                  className="btn btn-primary"
                  startIcon={<IconPlus />}
                  variant="contained"
                  onClick={() => {
                    setGroupName("");
                    setCompanyName("");
                    setLocation("");
                    setAllowNonKomatsuUsage(true);
                    setAllowUploadFromFS(true);
                    setIsSignatureRequired(false);
                    setErrorMessage(null);
                    setCompanyError(null);
                    setLocationError(null);
                    setOpenAddGroup(true);
                    setSelectedRowKeys([]);
                    setSelectedRowArchivedKeys([]);
                  }}
                  style={{
                    height: "45px",
                    fontSize: "14px",
                    marginRight: "10px",
                  }}
                >
                  {t("addGroup")}
                </Button>
              </div>
            )}

            {showArchivedList && (
              <div
                className="d-flex"
                style={{
                  flexDirection: isSmallScreen ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isSmallScreen ? "flex-start" : "center",
                  marginTop: "4px",
                  marginBottom: "8px",
                  gap: "10px",
                }}
              >
                <div className="d-flex flex-row align-items-center gap-2">
                  <GroupsOutlined
                    sx={{ color: "black", marginRight: "10px" }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t("activeGroup")}
                  </Typography>
                </div>

                <Button
                  className="btn btn-primary"
                  startIcon={<IconPlus />}
                  variant="contained"
                  onClick={() => {
                    setGroupName("");
                    setCompanyName("");
                    setLocation("");
                    setAllowNonKomatsuUsage(true);
                    setAllowUploadFromFS(true);
                    setIsSignatureRequired(false);
                    setErrorMessage(null);
                    setCompanyError(null);
                    setLocationError(null);
                    setOpenAddGroup(true);
                    setSelectedRowKeys([]);
                    setSelectedRowArchivedKeys([]);
                  }}
                  style={{
                    height: "45px",
                    fontSize: "14px",
                    marginRight: "10px",
                    marginTop: isSmallScreen ? "10px" : "0px",
                  }}
                >
                  {t("addGroup")}
                </Button>
              </div>
            )}
          </div>

          <DataTable
            rowKey="groupId"
            columns={columns}
            dataSource={rows}
            onRequestSort={handleSort}
            textEmptyData="noGroup"
            loading={isLoading}
          />

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {targetRow?.permissions?.allowEditDeleteGroup && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setIsSignatureRequired(
                    targetRow?.isSignatureRequired ?? false,
                  );
                  setOpenUpdateGroup(true);
                }}
              >
                <ListItemIcon>
                  <IconEdit />
                </ListItemIcon>
                <ListItemText primary={t("formAction.edit")} />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setOpenArchiveGroup(true);
              }}
            >
              <ListItemIcon>
                <ArchiveOutlined sx={{ color: "black" }} />
              </ListItemIcon>
              <ListItemText primary={t("archivedGroup")} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setOpenMoreInfo(true);
              }}
            >
              <ListItemIcon>
                <InfoOutlined sx={{ color: "black" }} />
              </ListItemIcon>
              <ListItemText primary={t("moreInfo")} />
            </MenuItem>
            {targetRow?.permissions?.allowEditDeleteGroup && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setOpenDeleteGroup(true);
                }}
              >
                <ListItemIcon>
                  <IconDelete />
                </ListItemIcon>

                <ListItemText
                  primary={t("formAction.delete")}
                  primaryTypographyProps={{ style: { color: "#DA093B" } }}
                />
              </MenuItem>
            )}
          </Menu>

          <DataPagination
            isHidden={!rows?.length}
            currentPage={pageInfo.page}
            limitPage={pageInfo.limit}
            totalPage={pageInfo.total}
            handleChangePage={handleChangePage}
            className="mt-25 sp-p24"
          />
        </div>

        {/* {!showArchivedList && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <Button
              onClick={() => setShowArchivedList(true)}
              sx={{
                backgroundColor: "#eaf4ff",
                border: "none",
                borderRadius: "20px",
                padding: "10px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                color: "black",
                fontSize: "14px",
                fontWeight: "500",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                transition: "background-color 0.3s ease",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#cce5ff",
                },
              }}
            >
              {t("showList")}
            </Button>
          </div>
        )} */}

        {showArchivedList && (
          <div className="row">
            <div className="mb-25 sp-p24">
              <div
                className="d-flex"
                style={{
                  flexDirection: isSmallScreen ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isSmallScreen ? "flex-start" : "center",
                  marginTop: "10px",
                  gap: "10px",
                }}
              >
                <div className="d-flex flex-row align-items-center gap-2">
                  <ArchiveOutlined
                    sx={{ color: "black", marginRight: "10px" }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t("archivedGroup")}
                  </Typography>
                </div>
              </div>
            </div>

            <DataTable
              rowKey="groupId"
              columns={archivedColumns}
              dataSource={archivedRows}
              onRequestSort={handleSort}
              textEmptyData="noGroup"
            />

            <Menu
              anchorEl={anchorElArchive}
              open={openArchive}
              onClose={() => setAnchorElArchive(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {targetRow?.permissions?.allowEditDeleteGroup && (
                <MenuItem
                  onClick={() => {
                    setAnchorElArchive(null);
                    setIsSignatureRequired(
                      targetRow?.isSignatureRequired ?? false,
                    );
                    setOpenUpdateGroup(true);
                  }}
                >
                  <ListItemIcon>
                    <IconEdit />
                  </ListItemIcon>
                  <ListItemText primary={t("formAction.edit")} />
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setAnchorElArchive(null);
                  setOpenUnArchiveGroup(true);
                }}
              >
                <ListItemIcon>
                  <ArchiveOutlined sx={{ color: "black" }} />
                </ListItemIcon>
                <ListItemText primary={t("unarchived")} />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorElArchive(null);
                  setOpenMoreInfo(true);
                }}
              >
                <ListItemIcon>
                  <InfoOutlined sx={{ color: "black" }} />
                </ListItemIcon>
                <ListItemText primary={t("moreInfo")} />
              </MenuItem>
              {targetRow?.permissions?.allowEditDeleteGroup && (
                <MenuItem
                  onClick={() => {
                    setAnchorElArchive(null);
                    setOpenDeleteGroup(true);
                  }}
                >
                  <ListItemIcon>
                    <IconDelete />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("formAction.delete")}
                    primaryTypographyProps={{ style: { color: "#DA093B" } }}
                  />
                </MenuItem>
              )}
            </Menu>

            <DataPagination
              isHidden={!archivedRows?.length}
              currentPage={pageArchivedInfo.page}
              limitPage={pageArchivedInfo.limit}
              totalPage={pageArchivedInfo.total}
              handleChangePage={handleChangePage}
              className="mt-25 sp-p24"
            />
            {/* <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <Button
                onClick={() => setShowArchivedList(false)}
                sx={{
                  backgroundColor: "#eaf4ff",
                  border: "none",
                  borderRadius: "20px",
                  padding: "10px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  color: "black",
                  fontSize: "14px",
                  fontWeight: "500",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  transition: "background-color 0.3s ease",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#cce5ff",
                  },
                }}
              >
                {t("dialogRefuel.close")}
              </Button>
            </div> */}
          </div>
        )}
      </div>

      <DataDialog
        open={openCancelConfirm}
        setOpen={setOpenCancelConfirm}
        disableBackdropClick={true}
      >
        <Typography variant="body1" sx={{ mb: 3, fontWeight: 700 }}>
          {t("cancelAddingGroup")}
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => setOpenCancelConfirm(false)}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            onClick={() => {
              setOpenAddGroup(false);
              setGroupName("");
              setCompanyName("");
              setLocation("");
              setErrorMessage(null);
              setCompanyError(null);
              setLocationError(null);
              resetLogoState();
              setOpenCancelConfirm(false);
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        open={openTOUPDF}
        setOpen={setOpenTOUPDF}
        dialogActions={
          <div className="flex align-items-center justify-content-end">
            <Button className="btn-primary" onClick={handleClickCloseTOUPDF}>
              <span>{t("OK")}</span>
            </Button>
          </div>
        }
      >
        {loadingLoader ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress size={40} sx={{ color: "#140a9a" }} />
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            style={{ width: "100%", height: "500px", border: "none" }}
            title="Terms of Use & Privacy Policy"
            onLoad={handleIframeLoad}
          />
        )}
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        open={openTOU}
        title={t("dialogDataUsage.title1")}
        setOpen={setOpenTOU}
        dialogActions={
          <div className="flex align-items-center justify-content-end">
            <Button
              className="btn-primary"
              onClick={() => PostTOURecordHistory()}
              disabled={isButtonDisabled}
            >
              {t("OK")}
            </Button>
          </div>
        }
      >
        <p className="fs-16 txt-gray-1 m-0">{t("dialogDataUsage.content1")}</p>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginTop: "10px",
            marginLeft: "-10px",
          }}
        >
          <FormControlLabel
            className="checkbox-field"
            control={
              <Checkbox
                checked={checkboxTOU}
                onChange={handleCheckboxChangeTOU}
              />
            }
            label={
              <span>
                {t("dialogDataUsage.label1I")}
                <button
                  type="button"
                  onClick={() => openDialogWithLink(touPdfUrlOpen)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#551a8b",
                    textDecoration: "underline",
                    cursor: "pointer",
                    marginLeft: "5px",
                    font: "inherit",
                  }}
                >
                  {t("dialogDataUsage.label1")}
                </button>
              </span>
            }
          />
          <FormControlLabel
            className="checkbox-field"
            control={
              <Checkbox
                checked={checkboxPriPol}
                onChange={handleCheckboxChangePrivacyPolicy}
              />
            }
            label={
              <span>
                {t("dialogDataUsage.label1I")}
                <button
                  type="button"
                  onClick={() => openDialogWithLink(privacyPolicyPdfUrlOpen)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#551a8b",
                    textDecoration: "underline",
                    cursor: "pointer",
                    marginLeft: "5px",
                    font: "inherit",
                  }}
                >
                  {t("privacyPolicy")}
                </button>
              </span>
            }
          />
        </div>
      </DataDialog>

      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("addGroup")}</span>
            {errorMessage && (
              <span style={{ color: "red", fontSize: "12px", fontWeight: 500 }}>
                {errorMessage}
              </span>
            )}
          </div>
        }
        open={openAddGroup}
        setOpen={setOpenAddGroup}
        disableBackdropClick={true}
      >
        <div>
          <TextField
            margin="dense"
            label={t("groupName")}
            className="input-field"
            value={groupName}
            required
            onChange={(e) => {
              const value = e.target.value;
              setGroupName(value);

              let error: string | null = null;

              if (!value) {
                error = t("required") + " *";
              } else if (!REGEX.commonNameFieldRegex.test(value)) {
                error = t("invalidCharacters");
              } else if (!REGEX.emojiRegex.test(value)) {
                error = t("emojisarenotallowed");
              }

              setErrorMessage(error);
            }}
            inputProps={{ maxLength: 1000 }}
            error={!!errorMessage}
          />
        </div>

        <div style={{ marginTop: "-10px", marginBottom: "20px" }}>
          <Alert
            severity="info"
            style={{
              marginTop: "8px",
              marginBottom: "12px",
              backgroundColor: "#E6F4FF",
              border: "1px solid #B3E5FC",
            }}
          >
            {t("groupInfo")}
          </Alert>
        </div>

        <div style={{ marginTop: "-10px" }}>
          <TextField
            margin="dense"
            label={t("tableGroup.companyName")}
            className="input-field"
            value={companyName}
            onChange={(e) => {
              const value = e.target.value;
              setCompanyName(value);

              let error: string | null = null;

              if (value) {
                if (!REGEX.commonNameFieldRegex.test(value)) {
                  error = t("invalidCharacters");
                } else if (!REGEX.emojiRegex.test(value)) {
                  error = t("emojisarenotallowed");
                }
              }

              setCompanyError(error);
            }}
            inputProps={{ maxLength: 1000 }}
            error={!!companyError}
            helperText={companyError || " "}
          />
        </div>

        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {t("groupLogo")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("groupLogoDescription")}
          </Typography>

          {logoPreviewUrl ? (
            <>
              <Box sx={GROUP_LOGO_PREVIEW_CARD_SX}>
                <Box
                  sx={{
                    cursor: "pointer",
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  onClick={(e) => setLogoMenuAnchor(e.currentTarget)}
                >
                  <Box sx={GROUP_LOGO_PREVIEW_IMAGE_SX}>
                    {!logoImageLoaded && (
                      <CircularProgress
                        size={20}
                        sx={{ position: "absolute", color: "#140a9a" }}
                      />
                    )}
                    <Box
                      component="img"
                      src={logoPreviewUrl}
                      alt={t("groupLogo")}
                      onLoad={() => setLogoImageLoaded(true)}
                      onError={() => setLogoImageLoaded(true)}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        opacity: logoImageLoaded ? 1 : 0,
                      }}
                    />
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setOpenRemoveLogoConfirm(true)}
                  size="small"
                  aria-label={t("formAction.delete")}
                >
                  <IconDeleteRed />
                </IconButton>
              </Box>

              <Menu
                anchorEl={logoMenuAnchor}
                open={!!logoMenuAnchor}
                onClose={() => setLogoMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                className="menu-preview-file"
              >
                <MenuItem
                  disableRipple
                  className="menu-item"
                  onClick={() => {
                    setLogoMenuAnchor(null);
                    setLogoPreviewViewerOpen(true);
                  }}
                >
                  <span className="fs-16 txt-black-1 pointer">
                    {t("preview")}
                  </span>
                </MenuItem>
                <MenuItem disableRipple className="menu-item">
                  <label htmlFor="group-logo-upload-add">
                    <span className="fs-16 txt-black-1 pointer">
                      {t("chooseFile")}
                    </span>
                    <input
                      id="group-logo-upload-add"
                      type="file"
                      accept=".png,.jpg,.jpeg,.heic"
                      hidden
                      onChange={(e) => {
                        handleLogoFileChange(e);
                        setLogoMenuAnchor(null);
                      }}
                    />
                  </label>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <div className="wrapper-upload-file" style={{ textAlign: "center" }}>
              <p className="txt-gray-1 fs-14 m-0">
                {t("groupLogoUploadHint")}
              </p>
              <p className="txt-gray-1 fs-14 m-0 mt-8">
                {t("groupLogoFormatsLabel")}
              </p>
              <p className="txt-gray-1 fs-14 m-0">
                {t("groupLogoFormats")}
              </p>
              <p className="txt-gray-1 fs-14 m-0 mt-8">
                {t("groupLogoRecommendation")}
              </p>

              <input
                id="group-logo-upload-add"
                type="file"
                accept=".png,.jpg,.jpeg,.heic"
                onChange={handleLogoFileChange}
                style={{ display: "none" }}
              />
              <FileUploadButton
                htmlFor="group-logo-upload-add"
                className="upload-button btn-secondary mt-8"
              >
                {t("uploadFile")}
              </FileUploadButton>
            </div>
          )}
          {logoError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {logoError}
            </Alert>
          )}
        </Box>

        <div style={{ marginTop: "-10px" }}>
          <TextField
            margin="dense"
            label={t("tableMachineDetail.location")}
            className="input-field"
            value={location}
            onChange={(e) => {
              const value = e.target.value;
              setLocation(value);

              let error: string | null = null;

              if (value) {
                if (!REGEX.commonNameFieldRegex.test(value)) {
                  error = t("invalidCharacters");
                } else if (!REGEX.emojiRegex.test(value)) {
                  error = t("emojisarenotallowed");
                }
              }

              setLocationError(error);
            }}
            inputProps={{ maxLength: 1000 }}
            error={!!locationError}
            helperText={locationError || " "}
          />
        </div>

        <Box sx={{ mt: 1.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {t("dataUsageSettingsForNonKomatsuProduct")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ pr: 2 }}>
              {t("allowNonKomatsuUsageNew")}
            </Typography>

            {/* <Switch
              className={`btn-switch ${allowNonKomatsuUsage ? "checked" : ""}`}
              checked={allowNonKomatsuUsage}
              onChange={(e) => setAllowNonKomatsuUsage(e.target.checked)}
            /> */}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t("allowUploadFromFS")}
            </Typography>

            <Switch
              checked={allowUploadFromFS}
              className={`btn-switch ${allowUploadFromFS ? "checked" : ""}`}
              onChange={(e) => setAllowUploadFromFS(e.target.checked)}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ pr: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("signatureFeatureSettings")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("signatureFeatureSettingsDescription")}
              </Typography>
            </Box>

            <Switch
              checked={isSignatureRequired}
              className={`btn-switch ${isSignatureRequired ? "checked" : ""}`}
              onChange={(e) => setIsSignatureRequired(e.target.checked)}
            />
          </Box>
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            onClick={() => {
              setOpenCancelConfirm(true);
            }}
            className="btn btn-no-border"
            style={{ marginRight: "10px" }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            disabled={
              !groupName || !!errorMessage || !!companyError || !!locationError
            }
            onClick={() => {
              handleAddGroup();
            }}
          >
            {t("formAction.add")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("updateGroup")}</span>
            {errorMessage && (
              <span style={{ color: "red", fontSize: "12px", fontWeight: 500 }}>
                {errorMessage}
              </span>
            )}
          </div>
        }
        open={openUpdateGroup}
        setOpen={setOpenUpdateGroup}
        disableBackdropClick={true}
      >
        <div>
          <TextField
            margin="dense"
            label={t("groupName")}
            className="input-field"
            value={groupName}
            required
            onChange={(e) => {
              const value = e.target.value;
              setGroupName(value);

              let error: string | null = null;

              if (!value) {
                error = t("required") + " *";
              } else if (!REGEX.commonNameFieldRegex.test(value)) {
                error = t("invalidCharacters");
              } else if (!REGEX.emojiRegex.test(value)) {
                error = t("emojisarenotallowed");
              }

              setErrorMessage(error);
            }}
            inputProps={{ maxLength: 1000 }}
            error={!!errorMessage}
            helperText={" "}
          />
        </div>

        <div style={{ marginTop: "-10px" }}>
          <TextField
            margin="dense"
            label={t("tableGroup.companyName")}
            className="input-field"
            value={companyName}
            onChange={(e) => {
              const value = e.target.value;
              setCompanyName(value);

              let error: string | null = null;

              if (value) {
                if (!REGEX.commonNameFieldRegex.test(value)) {
                  error = t("invalidCharacters");
                } else if (!REGEX.emojiRegex.test(value)) {
                  error = t("emojisarenotallowed");
                }
              }

              setCompanyError(error);
            }}
            inputProps={{ maxLength: 1000 }}
            error={!!companyError}
            helperText={companyError || " "}
          />
        </div>

        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {t("groupLogo")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("groupLogoDescription")}
          </Typography>

          {logoPreviewUrl ? (
            <>
              <Box sx={GROUP_LOGO_PREVIEW_CARD_SX}>
                <Box
                  sx={{
                    cursor: "pointer",
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  onClick={(e) => setLogoMenuAnchor(e.currentTarget)}
                >
                  <Box sx={GROUP_LOGO_PREVIEW_IMAGE_SX}>
                    {!logoImageLoaded && (
                      <CircularProgress
                        size={20}
                        sx={{ position: "absolute", color: "#140a9a" }}
                      />
                    )}
                    <Box
                      component="img"
                      src={logoPreviewUrl}
                      alt={t("groupLogo")}
                      onLoad={() => setLogoImageLoaded(true)}
                      onError={() => setLogoImageLoaded(true)}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        opacity: logoImageLoaded ? 1 : 0,
                      }}
                    />
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setOpenRemoveLogoConfirm(true)}
                  size="small"
                  aria-label={t("formAction.delete")}
                >
                  <IconDeleteRed />
                </IconButton>
              </Box>

              <Menu
                anchorEl={logoMenuAnchor}
                open={!!logoMenuAnchor}
                onClose={() => setLogoMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                className="menu-preview-file"
              >
                <MenuItem
                  disableRipple
                  className="menu-item"
                  onClick={() => {
                    setLogoMenuAnchor(null);
                    setLogoPreviewViewerOpen(true);
                  }}
                >
                  <span className="fs-16 txt-black-1 pointer">
                    {t("preview")}
                  </span>
                </MenuItem>
                <MenuItem disableRipple className="menu-item">
                  <label htmlFor="group-logo-upload-update">
                    <span className="fs-16 txt-black-1 pointer">
                      {t("chooseFile")}
                    </span>
                    <input
                      id="group-logo-upload-update"
                      type="file"
                      accept=".png,.jpg,.jpeg,.heic"
                      hidden
                      onChange={(e) => {
                        handleLogoFileChange(e);
                        setLogoMenuAnchor(null);
                      }}
                    />
                  </label>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <div className="wrapper-upload-file" style={{ textAlign: "center" }}>
              <p className="txt-gray-1 fs-14 m-0">
                {t("groupLogoUploadHint")}
              </p>
              <p className="txt-gray-1 fs-14 m-0 mt-8">
                {t("groupLogoFormatsLabel")}
              </p>
              <p className="txt-gray-1 fs-14 m-0">
                {t("groupLogoFormats")}
              </p>
              <p className="txt-gray-1 fs-14 m-0 mt-8">
                {t("groupLogoRecommendation")}
              </p>

              <input
                id="group-logo-upload-update"
                type="file"
                accept=".png,.jpg,.jpeg,.heic"
                onChange={handleLogoFileChange}
                style={{ display: "none" }}
              />
              <FileUploadButton
                htmlFor="group-logo-upload-update"
                className="upload-button btn-secondary mt-8"
              >
                {t("uploadFile")}
              </FileUploadButton>
            </div>
          )}
          {logoError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {logoError}
            </Alert>
          )}
        </Box>

        <div style={{ marginTop: "-10px" }}>
          <TextField
            margin="dense"
            label={t("tableMachineDetail.location")}
            className="input-field"
            value={location}
            onChange={(e) => {
              const value = e.target.value;
              setLocation(value);

              let error: string | null = null;

              if (value) {
                if (!REGEX.commonNameFieldRegex.test(value)) {
                  error = t("invalidCharacters");
                } else if (!REGEX.emojiRegex.test(value)) {
                  error = t("emojisarenotallowed");
                }
              }

              setLocationError(error);
            }}
            inputProps={{ maxLength: 1000 }}
            error={!!locationError}
            helperText={locationError || " "}
          />
        </div>

        <Box sx={{ mt: 1.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {t("dataUsageSettingsForNonKomatsuProduct")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ pr: 2 }}>
              {t("allowNonKomatsuUsage")}
            </Typography>

            <Switch
              className={`btn-switch ${allowNonKomatsuUsage ? "checked" : ""}`}
              checked={allowNonKomatsuUsage}
              onChange={(e) => setAllowNonKomatsuUsage(e.target.checked)}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t("allowUploadFromFS")}
            </Typography>

            <Switch
              checked={allowUploadFromFS}
              className={`btn-switch ${allowUploadFromFS ? "checked" : ""}`}
              onChange={(e) => setAllowUploadFromFS(e.target.checked)}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ pr: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("signatureFeatureSettings")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("signatureFeatureSettingsDescription")}
              </Typography>
            </Box>

            <Switch
              checked={isSignatureRequired}
              className={`btn-switch ${isSignatureRequired ? "checked" : ""}`}
              onChange={(e) => setIsSignatureRequired(e.target.checked)}
            />
          </Box>
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            onClick={() => {
              setOpenUpdateGroup(false);
              setGroupName("");
              setCompanyName("");
              setLocation("");
              setErrorMessage(null);
              setCompanyError(null);
              setLocationError(null);
              setSelectedRowKeys([]);
              setSelectedRowArchivedKeys([]);
              resetLogoState();
            }}
            className="btn btn-no-border"
            style={{ marginRight: "10px" }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            disabled={
              !groupName || !!errorMessage || !!companyError || !!locationError
            }
            onClick={() => {
              handleUpdateGroup();
            }}
          >
            {t("formAction.update")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("deleteGroups")}
        open={openFormDeleteBulk}
        setOpen={setOpenFormDeleteBulk}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("deleteSelectedGroups")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setOpenFormDeleteBulk(false);
                setSelectedRowKeys([]);
                setSelectedRowArchivedKeys([]);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary"
              onClick={() => {
                handleBulkGroupsDelete(
                  selectedRowKeys.concat(selectedRowArchivedKeys),
                );
              }}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("deleteGroup")}
        open={openDeleteGroup}
        setOpen={setOpenDeleteGroup}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("deleteSelectedGroup")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setOpenDeleteGroup(false);
                setSelectedRowKey([]);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary bg-red"
              onClick={() => {
                handleBulkGroupsDelete(selectedRowKey);
              }}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("groupLogoRemoveTitle")}
        open={openRemoveLogoConfirm}
        setOpen={setOpenRemoveLogoConfirm}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("groupLogoRemoveConfirm")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenRemoveLogoConfirm(false)}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary bg-red"
              onClick={handleRemoveLogo}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("archiveGroup")}
        open={openArchiveGroup}
        setOpen={setOpenArchiveGroup}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("archiveSelectedGroup")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setOpenArchiveGroup(false);
                setSelectedRowKey([]);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary"
              onClick={() => {
                handleBulkGroupsArchive(selectedRowKey);
              }}
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("unarchiveGroup")}
        open={openUnArchiveGroup}
        setOpen={setOpenUnArchiveGroup}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("unarchiveSelectedGroup")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setOpenUnArchiveGroup(false);
                setSelectedRowKey([]);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary"
              onClick={() => {
                handleBulkGroupsUnarchive(selectedRowKey);
              }}
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick
        title={t("moreInfo")}
        open={openMoreInfo}
        setOpen={setOpenMoreInfo}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("groupName")}
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
            {groupName || "-"}
          </Typography>
          <Divider sx={{ my: 1.5 }} />

          {logoPreviewUrl && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                {t("groupLogo")}
              </Typography>
              <Box
                sx={{
                  ...GROUP_LOGO_PREVIEW_IMAGE_SX,
                  justifyContent: "flex-start",
                  mt: 0.5,
                  cursor: "pointer",
                }}
                onClick={() => setLogoPreviewViewerOpen(true)}
              >
                {!logoImageLoaded && (
                  <CircularProgress
                    size={20}
                    sx={{ position: "absolute", color: "#140a9a" }}
                  />
                )}
                <Box
                  component="img"
                  src={logoPreviewUrl}
                  alt={t("groupLogo")}
                  onLoad={() => setLogoImageLoaded(true)}
                  onError={() => setLogoImageLoaded(true)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "left center",
                    opacity: logoImageLoaded ? 1 : 0,
                  }}
                />
              </Box>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}

          {companyName?.trim() && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                {t("tableGroup.companyName")}
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                {companyName}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}

          {location?.trim() && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                {t("tableMachineDetail.location")}
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                {location}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}

          <Typography variant="subtitle2" color="text.secondary">
            {t("dataUsageSettingsForNonKomatsuProduct")}
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
            {allowNonKomatsuUsage ? t("allowToUseData") : t("denyToUseData")}
          </Typography>
          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" color="text.secondary">
            {t("allowUploadFromFS")}
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
            {allowUploadFromFS ? t("on") : t("off")}
          </Typography>
          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" color="text.secondary">
            {t("signatureFeatureSettings")}
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
            {isSignatureRequired ? t("on") : t("off")}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            onClick={() => setOpenMoreInfo(false)}
            className="btn btn-no-border"
          >
            {t("dialogRefuel.close")}
          </Button>
        </Box>
      </DataDialog>

      {isShownDataUsagePopup && !suppressDataUsagePopup && <DataUsageDialog />}

      {typeof document !== "undefined" &&
        logoPreviewViewerOpen &&
        logoPreviewUrl &&
        createPortal(
          <CustomImageViewer
            imageUrl={logoPreviewUrl}
            onClose={() => setLogoPreviewViewerOpen(false)}
          />,
          document.body,
        )}

      <AnnouncementPopupModal
        announcements={announcements}
        announcementsLoading={announcementsLoading}
        openAnnouncements={openAnnouncements}
        setOpenAnnouncements={setOpenAnnouncements}
        isoLocaleCode={userIsoLocaleCode}
        userTimeFormat={userTimeFormat}
      />
    </div>
  );
}
